import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Orders API
export const ordersApi = {
  getAll: (params) => axios.get(`${API_URL}/orders`, { params }),
  exportExcel: (params) => axios.get(`${API_URL}/orders/export/excel`, { responseType: 'blob', params }),
  getById: (id) => axios.get(`${API_URL}/orders/${id}`),
  create: (data) => axios.post(`${API_URL}/orders`, data),
  update: (id, data) => axios.put(`${API_URL}/orders/${id}`, data),
  updateDeadlineConfig: (id, config) => axios.put(`${API_URL}/orders/${id}/deadline-config`, config),
  delete: (id) => axios.delete(`${API_URL}/orders/${id}`),
  updateStage: (id, data) => axios.post(`${API_URL}/orders/${id}/stages`, data),
  bulkUpdateStages: (id, data) => axios.put(`${API_URL}/orders/${id}/stages/bulk`, data),
  reconcileStages: () => axios.post(`${API_URL}/orders/reconcile-stages`),
  updateMaterials: (id, data) => axios.post(`${API_URL}/orders/${id}/materials`, data),
  addReceipt: (id, data) => axios.post(`${API_URL}/orders/${id}/receipts`, data),
  resolveFactoryRequest: (orderId, requestId) => axios.put(`${API_URL}/orders/${orderId}/factory-request/${requestId}/resolve`),
  resetFactoryAcceptance: (id) => axios.post(`${API_URL}/orders/${id}/factory-acceptance/reset`),
  dismissFromFactory: (id, hide) => axios.post(`${API_URL}/orders/${id}/factory-dismiss?hide=${hide ? 'true' : 'false'}`),
  // Movement tracking
  logMovement: (id, data) => axios.post(`${API_URL}/orders/${id}/movement`, data),
  logMaterialIssuance: (id, data) => axios.post(`${API_URL}/orders/${id}/material-issuance`, data),
  issueChainsFromBreakdown: (id) => axios.post(`${API_URL}/orders/${id}/chains/issue-from-breakdown`),
  moveToStock: (id, data) => axios.post(`${API_URL}/orders/${id}/move-to-stock`, data),
  updateMaterialIssuance: (orderId, issuanceId, materialType, data) =>
    axios.put(`${API_URL}/orders/${orderId}/material-issuance/${issuanceId}?material_type=${materialType}`, data),
  deleteMaterialIssuance: (orderId, issuanceId, materialType) =>
    axios.delete(`${API_URL}/orders/${orderId}/material-issuance/${issuanceId}?material_type=${materialType}`),
  // Pending receipts (factory → admin handoff with signature)
  acceptReceipt: (orderId, receiptId, body) =>
    axios.post(`${API_URL}/orders/${orderId}/pending-receipts/${receiptId}/accept`, body),
  rejectReceipt: (orderId, receiptId, body) =>
    axios.post(`${API_URL}/orders/${orderId}/pending-receipts/${receiptId}/reject`, body),
  getMovements: (id) => axios.get(`${API_URL}/orders/${id}/movements`),
  // Chat
  getChat: (id) => axios.get(`${API_URL}/orders/${id}/chat`),
  sendChat: (id, data) => axios.post(`${API_URL}/orders/${id}/chat`, data),
  // Sticker status
  updateStickerStatus: (id, data) => axios.post(`${API_URL}/orders/${id}/sticker-status`, data),
  saveGoldRateCut: (id, data) => axios.post(`${API_URL}/orders/${id}/gold-rate-cut`, data),
  uploadGoldRateScreenshot: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/orders/${id}/gold-rate-cut/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getGoldRateScreenshotUrl: (id) => {
    const token = localStorage.getItem('siddhi_token');
    return `${API_URL}/orders/${id}/gold-rate-cut/screenshot${token ? '?token=' + token : ''}`;
  },
  // Manufacturing Order Form (PDF/Excel) — gating factory acceptance.
  uploadManufacturingForm: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/orders/${id}/manufacturing-form`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteManufacturingForm: (id) => axios.delete(`${API_URL}/orders/${id}/manufacturing-form`),
  getManufacturingFormUrl: (id) => {
    const token = localStorage.getItem('siddhi_token');
    return `${API_URL}/orders/${id}/manufacturing-form/file${token ? '?token=' + token : ''}`;
  },
  // Factory Processing Sheet (Excel only) — second gate alongside Mfg Form.
  uploadFactoryProcessingSheet: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/orders/${id}/factory-processing-sheet`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteFactoryProcessingSheet: (id) => axios.delete(`${API_URL}/orders/${id}/factory-processing-sheet`),
  getFactoryProcessingSheetUrl: (id) => {
    const token = localStorage.getItem('siddhi_token');
    return `${API_URL}/orders/${id}/factory-processing-sheet/file${token ? '?token=' + token : ''}`;
  },
  // Archive
  toggleArchive: (id) => axios.post(`${API_URL}/orders/${id}/archive`),
  // Reorder
  reorder: (data) => axios.post(`${API_URL}/orders/reorder`, data),
  // Factory request chat
  addRequestMessage: (orderId, requestId, data) => axios.post(`${API_URL}/orders/${orderId}/requests/${requestId}/message`, data),
  // Resolve factory request
  resolveRequest: (orderId, requestId) => axios.put(`${API_URL}/orders/${orderId}/factory-request/${requestId}/resolve`),
  // Dispatch & Invoice
  createDispatch: (id, data) => axios.post(`${API_URL}/orders/${id}/dispatches`, data),
  getDispatches: (id) => axios.get(`${API_URL}/orders/${id}/dispatches`),
  convertMemoToInvoice: (orderId, dispatchId, data) => axios.put(`${API_URL}/orders/${orderId}/dispatches/${dispatchId}/invoice`, data),
  updateDispatch: (orderId, dispatchId, data) => axios.put(`${API_URL}/orders/${orderId}/dispatches/${dispatchId}`, data),
  // Invoice payments
  recordPayment: (orderId, dispatchId, data) => axios.post(`${API_URL}/orders/${orderId}/dispatches/${dispatchId}/payments`, data),
  updatePayment: (orderId, dispatchId, paymentId, data) => axios.put(`${API_URL}/orders/${orderId}/dispatches/${dispatchId}/payments/${paymentId}`, data),
  deletePayment: (orderId, dispatchId, paymentId) => axios.delete(`${API_URL}/orders/${orderId}/dispatches/${dispatchId}/payments/${paymentId}`),
  // PayForward early-payment supporting PDF
  downloadPayForward: (orderId, dispatchId) => axios.get(`${API_URL}/orders/${orderId}/dispatches/${dispatchId}/payforward.pdf`, { responseType: 'blob' }),
  // WhatsApp
  sendWhatsApp: (id, data) => axios.post(`${API_URL}/orders/${id}/whatsapp-notify`, data),
};

// Invoices API (debtor tracking)
export const invoicesApi = {
  getAll: () => axios.get(`${API_URL}/invoices`),
};

// Chain Inventory API
export const chainsApi = {
  getAll: () => axios.get(`${API_URL}/chains`),
  create: (data) => axios.post(`${API_URL}/chains`, data),
  update: (id, data) => axios.put(`${API_URL}/chains/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/chains/${id}`),
  adjustStock: (id, data) => axios.post(`${API_URL}/chains/${id}/stock`, data),
  getMovements: (id) => axios.get(`${API_URL}/chains/${id}/movements`),
};

// Messaging API
export const messagingApi = {
  heartbeat: () => axios.post(`${API_URL}/messages/heartbeat`),
  getPresence: () => axios.get(`${API_URL}/messages/presence`),
  getChannels: () => axios.get(`${API_URL}/messages/channels`),
  createChannel: (data) => axios.post(`${API_URL}/messages/channels`, data),
  getMessages: (channelId, limit = 50) => axios.get(`${API_URL}/messages/channels/${channelId}/messages?limit=${limit}`),
  sendMessage: (channelId, data) => axios.post(`${API_URL}/messages/channels/${channelId}/messages`, data),
  markRead: (channelId) => axios.post(`${API_URL}/messages/channels/${channelId}/read`),
  searchMentions: (q, type = 'all') => axios.get(`${API_URL}/messages/mention-search?q=${encodeURIComponent(q)}&type=${type}`),
};

// Factories API
export const factoriesApi = {
  getAll: () => axios.get(`${API_URL}/factories`),
  getById: (id) => axios.get(`${API_URL}/factories/${id}`),
  create: (data) => axios.post(`${API_URL}/factories`, data),
  update: (id, data) => axios.put(`${API_URL}/factories/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/factories/${id}`),
};

// Notifications API
export const notificationsApi = {
  getAll: (unreadOnly = false) => axios.get(`${API_URL}/notifications`, { params: { unread_only: unreadOnly } }),
  markRead: (id) => axios.put(`${API_URL}/notifications/${id}/read`),
  markAllRead: () => axios.put(`${API_URL}/notifications/read-all`),
  getPopups: () => axios.get(`${API_URL}/notifications/popups`),
  dismissPopups: () => axios.put(`${API_URL}/notifications/popups/dismiss`),
  checkDeadlines: () => axios.post(`${API_URL}/notifications/check-deadlines`),
};

// Analytics API
export const analyticsApi = {
  getDashboard: () => axios.get(`${API_URL}/analytics/dashboard`),
  getDashboardV2: (params) => axios.get(`${API_URL}/analytics/dashboard-v2`, { params }),
  getSalesDashboard: (params) => axios.get(`${API_URL}/analytics/sales-dashboard`, { params }),
  getSalesOrders: (params) => axios.get(`${API_URL}/analytics/sales-orders`, { params }),
  setSalesTarget: (monthKey, data) => axios.put(`${API_URL}/sales-targets/${monthKey}`, data),
  getSalesTarget: (monthKey) => axios.get(`${API_URL}/sales-targets/${monthKey}`),
  getTimeline: () => axios.get(`${API_URL}/timeline`),
  getMetalLedger: (params) => axios.get(`${API_URL}/metal-ledger`, { params }),
  getPendingGoldOrders: (params) => axios.get(`${API_URL}/metal-ledger/pending-orders`, { params }),
  createGoldDispatch: (data) => axios.post(`${API_URL}/metal-ledger/dispatch`, data),
  adjustGoldIssued: (orderId, newTotal, reason) => axios.put(`${API_URL}/metal-ledger/orders/${orderId}/adjust-gold-issued`, { new_total_issued: newTotal, reason: reason || '' }),
  deleteGoldDispatch: (entryId) => axios.delete(`${API_URL}/metal-ledger/${entryId}`),
  getSalesperson: () => axios.get(`${API_URL}/analytics/salesperson`),
  getMonthly: () => axios.get(`${API_URL}/analytics/monthly`),
  getAlerts: () => axios.get(`${API_URL}/analytics/alerts`),
  getBottlenecks: () => axios.get(`${API_URL}/analytics/bottlenecks`),
  getActivityFeed: (limit = 15) => axios.get(`${API_URL}/activity-feed`, { params: { limit } }),
};

// Settings API
export const settingsApi = {
  get: () => axios.get(`${API_URL}/settings`),
  update: (data) => axios.put(`${API_URL}/settings`, data),
  getWaTemplates: () => axios.get(`${API_URL}/settings/wa-templates`),
};

// Users API
export const usersApi = {
  getAll: () => axios.get(`${API_URL}/users`),
  getTeam: () => axios.get(`${API_URL}/users/team`),
  create: (data) => axios.post(`${API_URL}/users`, data),
  resetPassword: (id, newPassword) => axios.put(`${API_URL}/users/${id}/password`, { new_password: newPassword }),
  changeRole: (id, role) => axios.put(`${API_URL}/users/${id}/role`, { role }),
  setTabPermissions: (id, tabPermissions) => axios.put(`${API_URL}/users/${id}/tab-permissions`, { tab_permissions: tabPermissions }),
  delete: (id) => axios.delete(`${API_URL}/users/${id}`),
};

// Customer Activity Log API
export const customerActivityApi = {
  listContacts: (customerId) => axios.get(`${API_URL}/customers/${customerId}/contacts`),
  list: (customerId, params) => axios.get(`${API_URL}/customers/${customerId}/activities`, { params }),
  create: (customerId, data) => axios.post(`${API_URL}/customers/${customerId}/activities`, data),
  update: (customerId, activityId, data) => axios.put(`${API_URL}/customers/${customerId}/activities/${activityId}`, data),
  delete: (customerId, activityId) => axios.delete(`${API_URL}/customers/${customerId}/activities/${activityId}`),
  uploadAttachment: (customerId, activityId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/customers/${customerId}/activities/${activityId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAttachment: (customerId, activityId, attachmentId) =>
    axios.delete(`${API_URL}/customers/${customerId}/activities/${activityId}/attachments/${attachmentId}`),
  attachmentUrl: (customerId, activityId, attachmentId, token) =>
    `${API_URL}/customers/${customerId}/activities/${activityId}/attachments/${attachmentId}?token=${encodeURIComponent(token || '')}`,
  summary: () => axios.get(`${API_URL}/customer-activities/summary`),
  myFollowups: () => axios.get(`${API_URL}/customer-activities/my-followups`),
};

// Chain Outreach — mini CRM for reaching out to Champion customers.
export const chainOutreachApi = {
  list: () => axios.get(`${API_URL}/chain-outreaches`),
  get: (id) => axios.get(`${API_URL}/chain-outreaches/${id}`),
  create: (data) => axios.post(`${API_URL}/chain-outreaches`, data),
  markRow: (outreachId, rowId, data) =>
    axios.post(`${API_URL}/chain-outreaches/${outreachId}/rows/${rowId}/mark`, data),
  undoRow: (outreachId, rowId) =>
    axios.post(`${API_URL}/chain-outreaches/${outreachId}/rows/${rowId}/undo`),
  addCustomers: (outreachId, customerIds) =>
    axios.post(`${API_URL}/chain-outreaches/${outreachId}/customers`, { customer_ids: customerIds }),
  close: (id) => axios.post(`${API_URL}/chain-outreaches/${id}/close`),
  reopen: (id) => axios.post(`${API_URL}/chain-outreaches/${id}/reopen`),
  delete: (id) => axios.delete(`${API_URL}/chain-outreaches/${id}`),
};

// Exhibition+ — booth selections turned into branded WhatsApp PDF.
export const exhibitionsApi = {
  list: (params) => axios.get(`${API_URL}/exhibitions`, { params }),
  create: (data) => axios.post(`${API_URL}/exhibitions`, data),
  update: (id, data) => axios.put(`${API_URL}/exhibitions/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/exhibitions/${id}`),
  dashboard: (id) => axios.get(`${API_URL}/exhibitions/${id}/dashboard`),
  listSelections: (id) => axios.get(`${API_URL}/exhibitions/${id}/selections`),
  createSelection: (id, data) => axios.post(`${API_URL}/exhibitions/${id}/selections`, data),
  getSelection: (id, sid) => axios.get(`${API_URL}/exhibitions/${id}/selections/${sid}`),
  deleteSelection: (id, sid) => axios.delete(`${API_URL}/exhibitions/${id}/selections/${sid}`),
  editSelection: (id, sid, data) => axios.patch(`${API_URL}/exhibitions/${id}/selections/${sid}`, data),
  generatePreview: (id, sid) => axios.post(`${API_URL}/exhibitions/${id}/selections/${sid}/generate-preview`),
  uploadSelectionPdf: (id, sid, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API_URL}/exhibitions/${id}/selections/${sid}/upload-pdf`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadSelectionJewellery: (id, sid, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API_URL}/exhibitions/${id}/selections/${sid}/upload-jewellery`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  previewPdfUrl: (id, sid) => {
    const token = localStorage.getItem('siddhi_token');
    return `${API_URL}/exhibitions/${id}/selections/${sid}/preview.pdf${token ? `?token=${token}` : ''}`;
  },
  finalise: (id, sid, data) =>
    axios.post(`${API_URL}/exhibitions/${id}/selections/${sid}/finalise`, data),
  resend: (id, sid, data) =>
    axios.post(`${API_URL}/exhibitions/${id}/selections/${sid}/resend`, data),
  // Customer-logo helpers live under /customers/{id}/logo but belong to this flow.
  uploadCustomerLogo: (customerId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API_URL}/customers/${customerId}/logo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  customerLogoUrl: (customerId) => {
    const token = localStorage.getItem('siddhi_token');
    return `${API_URL}/customers/${customerId}/logo${token ? `?token=${token}` : ''}`;
  },
};

// Diamond inventory & stock register.
export const diamondsApi = {
  // Config
  listShapes: () => axios.get(`${API_URL}/diamonds/shapes`),
  createShape: (data) => axios.post(`${API_URL}/diamonds/shapes`, data),
  updateShape: (id, data) => axios.put(`${API_URL}/diamonds/shapes/${id}`, data),
  deleteShape: (id) => axios.delete(`${API_URL}/diamonds/shapes/${id}`),
  listQualities: () => axios.get(`${API_URL}/diamonds/qualities`),
  createQuality: (data) => axios.post(`${API_URL}/diamonds/qualities`, data),
  updateQuality: (id, data) => axios.put(`${API_URL}/diamonds/qualities/${id}`, data),
  deleteQuality: (id) => axios.delete(`${API_URL}/diamonds/qualities/${id}`),
  listSieves: (params) => axios.get(`${API_URL}/diamonds/sieves`, { params }),
  createSieve: (data) => axios.post(`${API_URL}/diamonds/sieves`, data),
  updateSieve: (id, data) => axios.put(`${API_URL}/diamonds/sieves/${id}`, data),
  deleteSieve: (id) => axios.delete(`${API_URL}/diamonds/sieves/${id}`),
  listBenchmarks: () => axios.get(`${API_URL}/diamonds/benchmarks`),
  upsertBenchmark: (data) => axios.post(`${API_URL}/diamonds/benchmarks`, data),
  deleteBenchmark: (id) => axios.delete(`${API_URL}/diamonds/benchmarks/${id}`),
  // Lots
  listLots: (params) => axios.get(`${API_URL}/diamonds/lots`, { params }),
  getLot: (id) => axios.get(`${API_URL}/diamonds/lots/${id}`),
  createLot: (data) => axios.post(`${API_URL}/diamonds/lots`, data),
  updateLot: (id, data) => axios.put(`${API_URL}/diamonds/lots/${id}`, data),
  deleteLot: (id) => axios.delete(`${API_URL}/diamonds/lots/${id}`),
  assortLot: (id, data) => axios.post(`${API_URL}/diamonds/lots/${id}/assort`, data),
  assortLotBulk: (id, data) => axios.post(`${API_URL}/diamonds/lots/${id}/assort-bulk`, data),
  issueFromLot: (id, data) => axios.post(`${API_URL}/diamonds/lots/${id}/issue`, data),
  // Stock
  listStock: () => axios.get(`${API_URL}/diamonds/stock`),
  issueFromStock: (data) => axios.post(`${API_URL}/diamonds/stock/issue`, data),
  adjustStock: (data) => axios.post(`${API_URL}/diamonds/stock/adjust`, data),
  // Movements + dashboard
  listMovements: (params) => axios.get(`${API_URL}/diamonds/movements`, { params }),
  dashboard: () => axios.get(`${API_URL}/diamonds/dashboard`),
  wipe: (data) => axios.post(`${API_URL}/diamonds/admin/wipe`, data),
};

// Customer Payments API — FIFO-allocated lump-sum receipt from a customer.
export const customerPaymentsApi = {
  // preview=true dry-runs the FIFO allocation without persisting
  receive: (customerId, data) =>
    axios.post(`${API_URL}/customers/${customerId}/receive-payment`, data),
};

// Quotations API
export const quotationsApi = {
  getAll: (params) => axios.get(`${API_URL}/quotations`, { params }),
  getById: (id) => axios.get(`${API_URL}/quotations/${id}`),
  create: (data) => axios.post(`${API_URL}/quotations`, data),
  update: (id, data) => axios.put(`${API_URL}/quotations/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/quotations/${id}`),
  upload: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/quotations/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  download: (id) => axios.get(`${API_URL}/quotations/${id}/download`, { responseType: 'blob' }),
  confirm: (id) => axios.post(`${API_URL}/quotations/${id}/confirm`),
  reject: (id) => axios.post(`${API_URL}/quotations/${id}/reject`),
  createOrder: (id) => axios.post(`${API_URL}/quotations/${id}/create-order`),
};

// Email Preferences
export const emailApi = {
  getPreferences: () => axios.get(`${API_URL}/email-preferences`),
  updatePreferences: (data) => axios.put(`${API_URL}/email-preferences`, data),
  sendDailyReport: () => axios.post(`${API_URL}/email/send-daily-report`),
  sendTest: () => axios.post(`${API_URL}/email/test`),
};

// Static Data API
export const saleshubApi = {
  getCustomers: () => axios.get(`${API_URL}/saleshub/customers`),
};

// Factory Portal API
export const factoryApi = {
  getOrders: () => axios.get(`${API_URL}/factory/orders`),
  exportLiveStatusXlsx: (includeDispatched = true) =>
    axios.get(`${API_URL}/factory/live-status.xlsx`, {
      responseType: 'blob',
      params: { include_dispatched: includeDispatched },
    }),
  getOrder: (id) => axios.get(`${API_URL}/factory/orders/${id}`),
  acceptOrder: (id) => axios.post(`${API_URL}/factory/orders/${id}/accept`),
  rejectOrder: (id, data) => axios.post(`${API_URL}/factory/orders/${id}/reject`, data),
  resetAcceptance: (id) => axios.post(`${API_URL}/orders/${id}/factory-acceptance/reset`),
  logMovement: (id, data) => axios.post(`${API_URL}/factory/orders/${id}/movement`, data),
  submitRequest: (id, data) => axios.post(`${API_URL}/factory/orders/${id}/request`, data),
  addRequestMessage: (orderId, requestId, data) => axios.post(`${API_URL}/orders/${orderId}/requests/${requestId}/message`, data),
  getManufacturingFormUrl: (id) => {
    const token = localStorage.getItem('siddhi_token');
    return `${API_URL}/orders/${id}/manufacturing-form/file${token ? '?token=' + token : ''}`;
  },
  getFactoryProcessingSheetUrl: (id) => {
    const token = localStorage.getItem('siddhi_token');
    return `${API_URL}/orders/${id}/factory-processing-sheet/file${token ? '?token=' + token : ''}`;
  },
};

export const staticApi = {
  getStages: () => axios.get(`${API_URL}/static/stages`),
  getPriorities: () => axios.get(`${API_URL}/static/priorities`),
  getDiamondQualities: () => axios.get(`${API_URL}/static/diamond-qualities`),
  getCertificationLabs: () => axios.get(`${API_URL}/static/certification-labs`),
};

// Customer Portal API
export const portalApi = {
  auth: (token) => axios.get(`${API_URL}/customer-portal/auth?token=${token}`),
  requestLink: (mobile) => axios.post(`${API_URL}/customer-portal/request-link?mobile=${mobile}`),
  register: (data) => axios.post(`${API_URL}/customer-portal/register`, data),
  me: () => axios.get(`${API_URL}/customer-portal/me`),
  saveKYC: (step, data) => axios.post(`${API_URL}/customer-portal/kyc`, { step, data }),
  getKYC: () => axios.get(`${API_URL}/customer-portal/kyc`),
  getOrders: () => axios.get(`${API_URL}/customer-portal/orders`),
  getOrder: (id) => axios.get(`${API_URL}/customer-portal/orders/${id}`),
  getDispatches: () => axios.get(`${API_URL}/customer-portal/dispatches`),
  createQuery: (data) => axios.post(`${API_URL}/customer-portal/queries`, data),
  getQueries: () => axios.get(`${API_URL}/customer-portal/queries`),
  replyQuery: (id, message) => axios.post(`${API_URL}/customer-portal/queries/${id}/reply`, { message }),
  getOrgTypes: () => axios.get(`${API_URL}/customer-portal/static/org-types`),
  getQueryCategories: () => axios.get(`${API_URL}/customer-portal/static/query-categories`),
  addContact: (data) => axios.post(`${API_URL}/customer-portal/phones`, data),
  // Linked companies (sister firms)
  listLinkedCompanies: () => axios.get(`${API_URL}/customer-portal/linked-companies`),
  switchCompany: (targetId) => axios.post(`${API_URL}/customer-portal/switch?target_customer_id=${encodeURIComponent(targetId)}`),
  // Ledger (self-service)
  getLedger: (params = {}) => axios.get(`${API_URL}/customer-portal/ledger`, { params }),
  getLedgerPdfUrl: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `${API_URL}/customer-portal/ledger.pdf${qs ? '?' + qs : ''}`;
  },
};

// Admin Customer Management
export const customersApi = {
  getAll: () => axios.get(`${API_URL}/customers`),
  getApprovedForOrder: () => axios.get(`${API_URL}/customers/approved-for-order`),
  get: (id) => axios.get(`${API_URL}/customers/${id}`),
  invite: (data) => axios.post(`${API_URL}/customers/invite`, data),
  update: (id, data) => axios.put(`${API_URL}/customers/${id}`, data),
  setChampion: (id, isChampion) => axios.put(`${API_URL}/customers/${id}`, { is_champion: !!isChampion }),
  updateKyc: (id, step, data) => axios.put(`${API_URL}/customers/${id}/kyc`, { step, data }),
  delete: (id) => axios.delete(`${API_URL}/customers/${id}`),
  replyQuery: (queryId, message) => axios.post(`${API_URL}/customers/queries/${queryId}/reply`, { message }),
  kycAction: (id, action, message) => axios.post(`${API_URL}/customers/${id}/kyc-action`, { action, message }),
  addPhone: (id, phone, label, extra) => axios.post(`${API_URL}/customers/${id}/phones`, { phone, label, ...(extra || {}) }),
  updatePhone: (id, oldPhone, body) => axios.put(`${API_URL}/customers/${id}/phones/${oldPhone}`, body),
  removePhone: (id, phone) => axios.delete(`${API_URL}/customers/${id}/phones/${phone}`),
  sendLoginLink: (id, phone) => axios.post(`${API_URL}/customers/${id}/send-login-link${phone ? `?phone=${encodeURIComponent(phone)}` : ''}`),
  // Documents
  listDocuments: (id) => axios.get(`${API_URL}/customers/${id}/documents`),
  uploadDocument: (id, file, category, label) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', category);
    if (label) fd.append('label', label);
    return axios.post(`${API_URL}/customers/${id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadDocument: (id, docId) => axios.get(`${API_URL}/customers/${id}/documents/${docId}/download`, { responseType: 'blob' }),
  // Linked companies
  listLinks: (id) => axios.get(`${API_URL}/customers/${id}/links`),
  link: (id, targetId) => axios.post(`${API_URL}/customers/${id}/link`, { target_customer_id: targetId }),
  unlink: (id, targetId) => axios.delete(`${API_URL}/customers/${id}/link/${targetId}`),
  deleteDocument: (id, docId) => axios.delete(`${API_URL}/customers/${id}/documents/${docId}`),
  importPreview: (file) => { const fd = new FormData(); fd.append('file', file); return axios.post(`${API_URL}/customers/import/preview`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  importFile: (file, columnMap) => {
    const fd = new FormData();
    fd.append('file', file);
    if (columnMap) fd.append('column_map', JSON.stringify(columnMap));
    return axios.post(`${API_URL}/customers/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  exportCSV: () => `${API_URL}/customers/export/csv`,
};

// ============ Receivables (AR) ============
export const arApi = {
  // Admin cleanup — legacy AR-module customer payments (super_admin only)
  getOrphanPayments: () => axios.get(`${API_URL}/ar/admin/orphan-payments`),
  purgeOrphanPayments: () => axios.post(`${API_URL}/ar/admin/orphan-payments/purge`),
  dashboard: () => axios.get(`${API_URL}/ar/dashboard`),
  invoices: (params) => axios.get(`${API_URL}/ar/invoices`, { params }),
  agingReport: (params) => axios.get(`${API_URL}/ar/aging-report`, { params }),
  cashForecast: (days = 60) => axios.get(`${API_URL}/ar/cash-forecast`, { params: { days } }),
  customerReceivables: (customerId) => axios.get(`${API_URL}/ar/customers/${customerId}/receivables`),
  customersReceivablesSummary: () => axios.get(`${API_URL}/ar/customers/receivables-summary`),
  // Active orders (this customer + linked branches)
  customerActiveOrders: (customerId) => axios.get(`${API_URL}/customers/${customerId}/active-orders`),
  // Adjustments + ledger
  adjustmentTypes: () => axios.get(`${API_URL}/ar/adjustments/types`),
  customerOpenInvoices: (customerId) => axios.get(`${API_URL}/ar/customers/${customerId}/open-invoices`),
  listAdjustments: (customerId) => axios.get(`${API_URL}/ar/customers/${customerId}/adjustments`),
  createAdjustment: (customerId, data) => axios.post(`${API_URL}/ar/customers/${customerId}/adjustments`, data),
  updateAdjustment: (customerId, adjId, data) => axios.put(`${API_URL}/ar/customers/${customerId}/adjustments/${adjId}`, data),
  voidAdjustment: (customerId, adjId, reason = '') => axios.delete(`${API_URL}/ar/customers/${customerId}/adjustments/${adjId}`, { params: { reason } }),
  customerLedger: (customerId, params = {}) => axios.get(`${API_URL}/ar/customers/${customerId}/ledger`, { params }),
  customerLedgerPdfUrl: (customerId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `${API_URL}/ar/customers/${customerId}/ledger.pdf${qs ? '?' + qs : ''}`;
  },
  // Send ledger via WhatsApp (DoubleTick)
  previewLedgerDataPoints: (customerId, params = {}) => axios.get(`${API_URL}/ar/customers/${customerId}/ledger-data-points`, { params }),
  sendLedgerWhatsApp: (customerId, data) => axios.post(`${API_URL}/ar/customers/${customerId}/send-ledger-whatsapp`, data),
  // Opening balances (Indian FY)
  listOpeningBalances: (customerId) => axios.get(`${API_URL}/ar/customers/${customerId}/opening-balances`),
  upsertOpeningBalance: (customerId, data) => axios.post(`${API_URL}/ar/customers/${customerId}/opening-balances`, data),
  voidOpeningBalance: (customerId, obId, reason = '') => axios.delete(`${API_URL}/ar/customers/${customerId}/opening-balances/${obId}`, { params: { reason } }),
  updateCustomerCredit: (customerId, data) => axios.put(`${API_URL}/ar/customers/${customerId}/credit`, data),
  recordPayment: (orderId, dispatchId, data) => axios.post(`${API_URL}/ar/invoices/${orderId}/${dispatchId}/payment`, data),
  deletePayment: (orderId, dispatchId, paymentId) => axios.delete(`${API_URL}/ar/invoices/${orderId}/${dispatchId}/payment/${paymentId}`),
  sendReminder: (orderId, dispatchId, data) => axios.post(`${API_URL}/ar/invoices/${orderId}/${dispatchId}/reminder`, data),
  // Consolidated statement reminder — all open invoices for one customer in a single WhatsApp message
  bulkReminderPreview: (customerId) => axios.get(`${API_URL}/ar/customers/${customerId}/bulk-reminder/preview`),
  bulkReminderPdfUrl: (customerId) => `${API_URL}/ar/customers/${customerId}/statement.pdf`,
  sendBulkReminder: (customerId, data) => axios.post(`${API_URL}/ar/customers/${customerId}/bulk-reminder`, data),
  invoiceReminders: (orderId, dispatchId) => axios.get(`${API_URL}/ar/invoices/${orderId}/${dispatchId}/reminders`),
  createDispute: (orderId, dispatchId, data) => axios.post(`${API_URL}/ar/invoices/${orderId}/${dispatchId}/dispute`, data),
  resolveDispute: (orderId, dispatchId) => axios.post(`${API_URL}/ar/invoices/${orderId}/${dispatchId}/resolve-dispute`),
  listDisputes: (status = 'open') => axios.get(`${API_URL}/ar/disputes`, { params: { status } }),
  createPTP: (orderId, dispatchId, data) => axios.post(`${API_URL}/ar/invoices/${orderId}/${dispatchId}/ptp`, data),
  listPTP: (status = 'active') => axios.get(`${API_URL}/ar/ptp`, { params: { status } }),
  updatePTP: (ptpId, status) => axios.put(`${API_URL}/ar/ptp/${ptpId}?status=${status}`),
  getSettings: () => axios.get(`${API_URL}/ar/settings`),
  updateSettings: (data) => axios.put(`${API_URL}/ar/settings`, data),
  runAutoReminders: () => axios.post(`${API_URL}/ar/run-auto-reminders`),
  worklistToday: () => axios.get(`${API_URL}/ar/worklist/today`),
  worklistGenerate: () => axios.post(`${API_URL}/ar/worklist/generate`),
  worklistUpdateItem: (itemId, action, note = '') => axios.put(`${API_URL}/ar/worklist/items/${itemId}?action=${action}${note ? `&note=${encodeURIComponent(note)}` : ''}`),
};

// ============ Lab Planner ============
export const labApi = {
  forecast: (days = 14) => axios.get(`${API_URL}/labs/forecast`, { params: { days } }),
  recommendations: () => axios.get(`${API_URL}/labs/recommendations`),
  regenerate: () => axios.post(`${API_URL}/labs/recommendations/regenerate`),
  bookings: (params) => axios.get(`${API_URL}/labs/bookings`, { params }),
  createBooking: (data) => axios.post(`${API_URL}/labs/bookings`, data),
  updateBooking: (id, data) => axios.put(`${API_URL}/labs/bookings/${id}`, data),
  deleteBooking: (id) => axios.delete(`${API_URL}/labs/bookings/${id}`),
  settings: () => axios.get(`${API_URL}/labs/settings`),
  updateLabSettings: (lab, data) => axios.put(`${API_URL}/labs/settings/${lab}`, data),
};

// ============ Quotation Requests ============
export const quotationRequestApi = {
  list: () => axios.get(`${API_URL}/quotation-requests`),
  get: (id) => axios.get(`${API_URL}/quotation-requests/${id}`),
  create: (data) => axios.post(`${API_URL}/quotation-requests`, data),
  update: (id, data) => axios.put(`${API_URL}/quotation-requests/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/quotation-requests/${id}`),
  addItem: (id, data) => axios.post(`${API_URL}/quotation-requests/${id}/items`, data),
  updateItem: (id, itemId, data) => axios.put(`${API_URL}/quotation-requests/${id}/items/${itemId}`, data),
  deleteItem: (id, itemId) => axios.delete(`${API_URL}/quotation-requests/${id}/items/${itemId}`),
  exportSingle: (id) => axios.get(`${API_URL}/quotation-requests/${id}/export/excel`, { responseType: 'blob' }),
  exportAll: () => axios.get(`${API_URL}/quotation-requests/export/excel`, { responseType: 'blob' }),
};

// ============ Chain Purchase Orders ============
export const chainPoApi = {
  // Vendors
  listVendors: () => axios.get(`${API_URL}/chain-vendors`),
  createVendor: (data) => axios.post(`${API_URL}/chain-vendors`, data),
  updateVendor: (id, data) => axios.put(`${API_URL}/chain-vendors/${id}`, data),
  deleteVendor: (id) => axios.delete(`${API_URL}/chain-vendors/${id}`),
  // POs
  listPos: (params = {}) => axios.get(`${API_URL}/chain-pos`, { params }),
  getPo: (id) => axios.get(`${API_URL}/chain-pos/${id}`),
  createPo: (data) => axios.post(`${API_URL}/chain-pos`, data),
  updatePo: (id, data) => axios.put(`${API_URL}/chain-pos/${id}`, data),
  deletePo: (id) => axios.delete(`${API_URL}/chain-pos/${id}`),
  sendPo: (id) => axios.post(`${API_URL}/chain-pos/${id}/send`),
  receivePo: (id, data) => axios.post(`${API_URL}/chain-pos/${id}/receive`, data),
  closePo: (id) => axios.post(`${API_URL}/chain-pos/${id}/close`),
  remindPo: (id) => axios.post(`${API_URL}/chain-pos/${id}/remind`),
  downloadPoPdf: (id) => axios.get(`${API_URL}/chain-pos/${id}/pdf`, { responseType: 'blob' }),
  // Spring hooks
  listSpringHooks: (vendorId) => axios.get(`${API_URL}/chain-vendors/${vendorId}/spring-hooks`),
  issueSpringHooks: (vendorId, data) => axios.post(`${API_URL}/chain-vendors/${vendorId}/spring-hooks`, data),
  deleteSpringHook: (vendorId, id) => axios.delete(`${API_URL}/chain-vendors/${vendorId}/spring-hooks/${id}`),
};


